/**
 * Admin : gestion du fil d'actualité du portail étudiant.
 * - Liste des actualités (publiées / brouillons)
 * - Création / modification (titre, type, contenu, image, ciblage)
 * - Push automatique aux étudiants ciblés à la publication
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, Edit2, Trash2, Pin, Eye, EyeOff, Calendar, MapPin, Image as ImageIcon,
  Filter, X, Users as UsersIcon, Loader2, Send,
} from 'lucide-react';
import {
  useListActualitesAdminQuery,
  useCreateActualiteMutation,
  useUpdateActualiteMutation,
  useDeleteActualiteMutation,
  ActualiteAdminItem,
} from '../../services/portailAdminApi';
import {
  useGetClassesQuery,
  useGetOptionsQuery,
} from '../../services/polesOptionsApi';

const TYPE_CHOICES = [
  { value: 'info', label: 'Information' },
  { value: 'seminaire', label: 'Séminaire' },
  { value: 'cours', label: 'Cours / Programme' },
  { value: 'soutenance', label: 'Soutenance' },
  { value: 'excursion', label: 'Excursion / Sortie' },
  { value: 'bal', label: 'Évènement festif / Bal' },
  { value: 'calendrier', label: 'Calendrier académique' },
  { value: 'livre', label: 'Publication / Livre' },
  { value: 'projet', label: 'Présentation projet' },
  { value: 'autre', label: 'Autre' },
];

const NIVEAUX = ['L1', 'L2', 'L3', 'M1', 'M2'];

function fmtDate(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export const Actualites: React.FC = () => {
  const [statut, setStatut] = useState<string>('');
  const [typeFiltre, setTypeFiltre] = useState<string>('');
  const [auteurFiltre, setAuteurFiltre] = useState<string>('');
  const [editing, setEditing] = useState<ActualiteAdminItem | null>(null);
  const [showForm, setShowForm] = useState(false);

  const { data, isLoading, refetch } = useListActualitesAdminQuery({
    statut: statut || undefined,
    type: typeFiltre || undefined,
    auteur_role: auteurFiltre || undefined,
  });
  const [delMut, { isLoading: deleting }] = useDeleteActualiteMutation();

  const handleDelete = async (it: ActualiteAdminItem) => {
    if (!window.confirm(`Supprimer l'actualité « ${it.titre} » ? Cette action est irréversible.`)) return;
    try {
      await delMut(it.id).unwrap();
      refetch();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur suppression');
    }
  };

  const openNew = () => {
    setEditing(null);
    setShowForm(true);
  };

  const openEdit = (it: ActualiteAdminItem) => {
    setEditing(it);
    setShowForm(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Fil d'actualité
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Publiez des annonces, séminaires, évènements à destination des étudiants.
            Une notification push est envoyée automatiquement aux étudiants ciblés à la publication.
          </p>
        </div>
        <button
          onClick={openNew}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium"
        >
          <Plus size={16} /> Nouvelle actualité
        </button>
      </div>

      {/* Filtres */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-3 flex items-center gap-3 flex-wrap text-sm">
        <Filter size={14} className="text-gray-500" />
        <select
          value={statut}
          onChange={(e) => setStatut(e.target.value)}
          className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
        >
          <option value="">Tous statuts</option>
          <option value="publiees">Publiées</option>
          <option value="brouillons">Brouillons</option>
        </select>
        <select
          value={typeFiltre}
          onChange={(e) => setTypeFiltre(e.target.value)}
          className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
        >
          <option value="">Tous types</option>
          {TYPE_CHOICES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <select
          value={auteurFiltre}
          onChange={(e) => setAuteurFiltre(e.target.value)}
          className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          title="Filtrer par rôle de l'auteur"
        >
          <option value="">Tous auteurs</option>
          <option value="formateur">Publié par formateur</option>
          <option value="admin">Publié par admin</option>
          <option value="super_admin">Publié par super admin</option>
        </select>
        {data && (
          <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
            {data.total} résultat{data.total > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Liste */}
      {isLoading ? (
        <div className="text-center py-12">
          <Loader2 className="inline-block animate-spin text-blue-500" size={28} />
          <p className="text-gray-500 text-sm mt-3">Chargement...</p>
        </div>
      ) : !data || data.results.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center text-gray-500 text-sm">
          Aucune actualité. Cliquez sur « Nouvelle actualité » pour publier la première.
        </div>
      ) : (
        <div className="space-y-2">
          {data.results.map((it) => (
            <ActualiteRow
              key={it.id}
              item={it}
              onEdit={() => openEdit(it)}
              onDelete={() => handleDelete(it)}
              deleting={deleting}
            />
          ))}
        </div>
      )}

      {showForm && (
        <ActualiteForm
          editing={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            refetch();
          }}
        />
      )}
    </div>
  );
};

// ─── Ligne liste ─────────────────────────────────────────────────

const ActualiteRow: React.FC<{
  item: ActualiteAdminItem;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
}> = ({ item, onEdit, onDelete, deleting }) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 flex items-start gap-3">
      {item.image_url ? (
        <img
          src={item.image_url}
          alt=""
          className="w-20 h-20 object-cover rounded border border-gray-200 dark:border-gray-700 flex-shrink-0"
        />
      ) : (
        <div className="w-20 h-20 rounded bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 flex items-center justify-center flex-shrink-0">
          <ImageIcon size={20} className="text-gray-400" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-semibold uppercase tracking-wide bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">
            {item.type_label}
          </span>
          {item.epinglee && (
            <span className="text-[10px] font-semibold inline-flex items-center gap-1 bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 px-1.5 py-0.5 rounded">
              <Pin size={9} /> Épinglée
            </span>
          )}
          {item.publiee ? (
            <span className="text-[10px] font-semibold inline-flex items-center gap-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded">
              <Eye size={9} /> Publiée
            </span>
          ) : (
            <span className="text-[10px] font-semibold inline-flex items-center gap-1 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded">
              <EyeOff size={9} /> Brouillon
            </span>
          )}
          {item.tous_etudiants ? (
            <span className="text-[10px] inline-flex items-center gap-1 text-gray-500">
              <UsersIcon size={9} /> Tous
            </span>
          ) : (
            <span className="text-[10px] inline-flex items-center gap-1 text-gray-500">
              <UsersIcon size={9} /> Ciblée
            </span>
          )}
        </div>
        <h3 className="font-semibold text-sm text-gray-900 dark:text-white mt-1 truncate">
          {item.titre}
        </h3>
        {item.contenu && (
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">
            {item.contenu}
          </p>
        )}
        <div className="mt-1.5 flex items-center gap-3 text-[11px] text-gray-500 flex-wrap">
          <span>{fmtDate(item.date_creation)}</span>
          {item.auteur_nom && <span>· {item.auteur_nom}</span>}
          {item.date_evenement && (
            <span className="inline-flex items-center gap-1">
              <Calendar size={10} /> {fmtDate(item.date_evenement)}
            </span>
          )}
          {item.lieu && (
            <span className="inline-flex items-center gap-1">
              <MapPin size={10} /> {item.lieu}
            </span>
          )}
          {(item.nb_lues ?? 0) > 0 && (
            <span>· {item.nb_lues} lecture{(item.nb_lues || 0) > 1 ? 's' : ''}</span>
          )}
          {item.nb_reactions_total > 0 && (
            <span>· {item.nb_reactions_total} réaction{item.nb_reactions_total > 1 ? 's' : ''}</span>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1.5 flex-shrink-0">
        <button
          onClick={onEdit}
          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
          title="Modifier"
        >
          <Edit2 size={15} />
        </button>
        <button
          onClick={onDelete}
          disabled={deleting}
          className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 disabled:opacity-50"
          title="Supprimer"
        >
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
};

// ─── Formulaire création / modification ──────────────────────────

const ActualiteForm: React.FC<{
  editing: ActualiteAdminItem | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ editing, onClose, onSaved }) => {
  const [type, setType] = useState(editing?.type || 'info');
  const [titre, setTitre] = useState(editing?.titre || '');
  const [contenu, setContenu] = useState(editing?.contenu || '');
  const [lienExterne, setLienExterne] = useState(editing?.lien_externe || '');
  const [dateEvenement, setDateEvenement] = useState<string>(
    editing?.date_evenement ? editing.date_evenement.slice(0, 16) : ''
  );
  const [lieu, setLieu] = useState(editing?.lieu || '');
  const [tousEtudiants, setTousEtudiants] = useState<boolean>(editing ? editing.tous_etudiants : true);
  const [niveauxCibles, setNiveauxCibles] = useState<string[]>(editing?.niveaux_cibles || []);
  const [classesCibles, setClassesCibles] = useState<string[]>(editing?.classes_cibles || []);
  const [optionsCibles, setOptionsCibles] = useState<string[]>(editing?.options_cibles || []);
  const [polesCibles, setPolesCibles] = useState<string[]>(editing?.poles_cibles || []);
  const [audience, setAudience] = useState<'tous' | 'etudiants' | 'parents' | 'formateurs'>(
    (editing?.audience as any) || 'tous',
  );
  const [pourFormateurs, setPourFormateurs] = useState<boolean>(
    !!(editing as any)?.pour_formateurs,
  );
  const [publiee, setPubliee] = useState<boolean>(editing ? editing.publiee : true);
  const [epinglee, setEpinglee] = useState<boolean>(editing ? editing.epinglee : false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [supprimerImage, setSupprimerImage] = useState(false);

  const [createMut, { isLoading: creating }] = useCreateActualiteMutation();
  const [updateMut, { isLoading: updating }] = useUpdateActualiteMutation();

  const { data: classes } = useGetClassesQuery({ actif: true });
  const { data: options } = useGetOptionsQuery({ actif: true });

  const classesParNiveau = useMemo(() => {
    const m = new Map<string, { code: string; nom: string }[]>();
    (classes || []).forEach((c) => {
      if (!m.has(c.niveau)) m.set(c.niveau, []);
      m.get(c.niveau)!.push({ code: c.code, nom: `${c.code}` });
    });
    return m;
  }, [classes]);

  const toggleInList = (
    list: string[], setter: (v: string[]) => void, val: string,
  ) => {
    setter(list.includes(val) ? list.filter((v) => v !== val) : [...list, val]);
  };

  const handleSubmit = async (publiee_now?: boolean) => {
    const fd = new FormData();
    fd.append('type', type);
    fd.append('titre', titre.trim());
    fd.append('contenu', contenu);
    fd.append('lien_externe', lienExterne);
    if (dateEvenement) fd.append('date_evenement', dateEvenement);
    fd.append('lieu', lieu);
    fd.append('tous_etudiants', tousEtudiants ? 'true' : 'false');
    niveauxCibles.forEach((n) => fd.append('niveaux_cibles', n));
    classesCibles.forEach((c) => fd.append('classes_cibles', c));
    optionsCibles.forEach((o) => fd.append('options_cibles', o));
    polesCibles.forEach((p) => fd.append('poles_cibles', p));
    fd.append('audience', audience);
    fd.append('pour_formateurs', pourFormateurs ? 'true' : 'false');
    fd.append('publiee', (publiee_now ?? publiee) ? 'true' : 'false');
    fd.append('epinglee', epinglee ? 'true' : 'false');
    if (imageFile) fd.append('image', imageFile);
    if (editing && supprimerImage && !imageFile) fd.append('supprimer_image', 'true');

    try {
      if (editing) {
        await updateMut({ id: editing.id, data: fd }).unwrap();
      } else {
        await createMut(fd).unwrap();
      }
      onSaved();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur enregistrement');
    }
  };

  const isLoading = creating || updating;
  const titreInvalide = !titre.trim();

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-3xl my-8 max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-5 py-3 flex items-center justify-between z-10">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {editing ? 'Modifier l\'actualité' : 'Nouvelle actualité'}
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Type + Titre */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Type
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
              >
                {TYPE_CHOICES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Titre <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={titre}
                onChange={(e) => setTitre(e.target.value)}
                maxLength={200}
                placeholder="Ex : Conférence sur l'IA — Amphi Polytechnique"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Contenu */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Contenu / Description
            </label>
            <textarea
              value={contenu}
              onChange={(e) => setContenu(e.target.value)}
              rows={5}
              placeholder="Détails de l'annonce, programme, intervenants, etc."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
            />
          </div>

          {/* Date événement + Lieu */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date / heure de l'évènement (optionnel)
              </label>
              <input
                type="datetime-local"
                value={dateEvenement}
                onChange={(e) => setDateEvenement(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Lieu (optionnel)
              </label>
              <input
                type="text"
                value={lieu}
                onChange={(e) => setLieu(e.target.value)}
                maxLength={300}
                placeholder="Ex : Amphi A, Campus de Cocody"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Lien externe */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Lien externe (optionnel)
            </label>
            <input
              type="url"
              value={lienExterne}
              onChange={(e) => setLienExterne(e.target.value)}
              maxLength={500}
              placeholder="https://..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
            />
          </div>

          {/* Image */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Image (optionnelle, max 5 MB)
            </label>
            {editing?.image_url && !imageFile && !supprimerImage && (
              <div className="mb-2 flex items-center gap-3">
                <img
                  src={editing.image_url}
                  alt=""
                  className="w-24 h-24 object-cover rounded border border-gray-300 dark:border-gray-600"
                />
                <button
                  type="button"
                  onClick={() => setSupprimerImage(true)}
                  className="text-xs text-red-600 hover:text-red-700 underline"
                >
                  Supprimer cette image
                </button>
              </div>
            )}
            {supprimerImage && (
              <div className="mb-2 text-xs text-red-600">
                L'image actuelle sera supprimée.
                <button
                  type="button"
                  onClick={() => setSupprimerImage(false)}
                  className="ml-2 underline"
                >
                  Annuler
                </button>
              </div>
            )}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => setImageFile(e.target.files?.[0] || null)}
              className="block w-full text-xs text-gray-700 dark:text-gray-300
                file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0
                file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
            />
            {imageFile && (
              <div className="mt-1 text-[11px] text-gray-500">{imageFile.name}</div>
            )}
          </div>

          {/* Audience (qui voit l'actu) */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <div className="text-[11px] font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Audience
            </div>
            <div className="flex flex-wrap gap-1.5">
              {([
                { v: 'tous', label: 'Étudiants + Parents' },
                { v: 'etudiants', label: 'Étudiants uniquement' },
                { v: 'parents', label: 'Parents uniquement' },
                { v: 'formateurs', label: 'Formateurs uniquement' },
              ] as const).map(opt => (
                <button
                  key={opt.v} type="button"
                  onClick={() => setAudience(opt.v)}
                  className={`px-3 py-1.5 text-xs rounded ${
                    audience === opt.v
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}>
                  {opt.label}
                </button>
              ))}
            </div>
            {/* Visibilité étendue aux formateurs (utile pour annonces direction
                qui concernent aussi les profs en plus des étudiants/parents) */}
            <label className="flex items-center gap-2 mt-2.5 pt-2.5 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
              <input type="checkbox" checked={pourFormateurs}
                onChange={(e) => setPourFormateurs(e.target.checked)}
                className="rounded" />
              Aussi visible dans le fil des formateurs
              <span className="text-[10px] text-gray-500 ml-1">(paiement émoluments, réunion, calendrier…)</span>
            </label>
          </div>

          {/* Ciblage géographique */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={tousEtudiants}
                onChange={(e) => setTousEtudiants(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                Sans ciblage (tous les pôles / niveaux / classes / options)
              </span>
            </label>
            {!tousEtudiants && (
              <div className="space-y-3 mt-3 pl-2 border-l-2 border-gray-200 dark:border-gray-700">
                {/* Niveaux */}
                <div>
                  <div className="text-[11px] font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Niveaux ciblés
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {NIVEAUX.map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => toggleInList(niveauxCibles, setNiveauxCibles, n)}
                        className={`px-2.5 py-1 text-xs font-medium rounded border ${
                          niveauxCibles.includes(n)
                            ? 'bg-blue-600 border-blue-500 text-white'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Pôles */}
                <div>
                  <div className="text-[11px] font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Pôles ciblés
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {['Polytechnique', 'Commerce', 'Juridique'].map((p) => (
                      <button
                        key={p} type="button"
                        onClick={() => toggleInList(polesCibles, setPolesCibles, p)}
                        className={`px-2 py-1 text-[11px] font-medium rounded border ${
                          polesCibles.includes(p)
                            ? 'bg-blue-600 border-blue-500 text-white'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                        }`}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Options */}
                <div>
                  <div className="text-[11px] font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Options ciblées
                  </div>
                  <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                    {(options || []).map((o: any) => (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => toggleInList(optionsCibles, setOptionsCibles, o.code)}
                        className={`px-2 py-1 text-[11px] font-medium rounded border ${
                          optionsCibles.includes(o.code)
                            ? 'bg-blue-600 border-blue-500 text-white'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                        }`}
                        title={o.nom}
                      >
                        {o.code}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Classes */}
                <div>
                  <div className="text-[11px] font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Classes ciblées
                  </div>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {Array.from(classesParNiveau.entries()).map(([niv, list]) => (
                      <div key={niv}>
                        <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                          {niv}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {list.map((c) => (
                            <button
                              key={c.code}
                              type="button"
                              onClick={() => toggleInList(classesCibles, setClassesCibles, c.code)}
                              className={`px-2 py-0.5 text-[11px] font-medium rounded border ${
                                classesCibles.includes(c.code)
                                  ? 'bg-blue-600 border-blue-500 text-white'
                                  : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                              }`}
                            >
                              {c.code}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {(niveauxCibles.length === 0 && classesCibles.length === 0 && optionsCibles.length === 0) && (
                  <div className="text-xs text-orange-600 dark:text-orange-400">
                    Aucun ciblage : l'actualité ne sera visible par personne.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Options publication */}
          <div className="flex flex-wrap items-center gap-4">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={publiee}
                onChange={(e) => setPubliee(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-gray-900 dark:text-white">Publier immédiatement</span>
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={epinglee}
                onChange={(e) => setEpinglee(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-gray-900 dark:text-white">Épingler en haut du fil</span>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-5 py-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            Annuler
          </button>
          {!editing && (
            <button
              type="button"
              onClick={() => {
                setPubliee(false);
                handleSubmit(false);
              }}
              disabled={isLoading || titreInvalide}
              className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded disabled:opacity-50"
            >
              Enregistrer en brouillon
            </button>
          )}
          <button
            type="button"
            onClick={() => handleSubmit()}
            disabled={isLoading || titreInvalide}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium inline-flex items-center gap-2 disabled:opacity-50"
          >
            {isLoading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {editing ? 'Enregistrer' : (publiee ? 'Publier (+ push)' : 'Enregistrer')}
          </button>
        </div>
      </div>
    </div>
  );
};
